import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelectedPlayers } from '../contexts/SelectedPlayersContext';
import { saveWorkoutCompletion } from '../firebase';
import Confetti from 'react-confetti';
import { getVersionedAudioUrl } from '../utils/audioUrl';
import { getSkillById } from '../data/new-skills';
import './DrillScreen.css';

const SkillScreen = ({ addPoints }) => {
  const { skillId, sport: urlSport } = useParams();
  const navigate = useNavigate();
  const { selectedPlayersData } = useSelectedPlayers();

  const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [showIntroScreen, setShowIntroScreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [currentExerciseData, setCurrentExerciseData] = useState(null);
  const [skillData, setSkillData] = useState(null);
  const [drillData, setDrillData] = useState(null);

  // Audio ref
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Load skill data
  useEffect(() => {
    const skill = getSkillById(parseInt(skillId));
    if (skill) {
      setSkillData(skill);
      // Load the first drill
      loadDrillData(skill.drills[0]);
    }
  }, [skillId]);

  // Load drill data for a specific drill ID
  const loadDrillData = async (drillId) => {
    try {
      // Import drill data dynamically based on sport
      const sport = skillData?.sport || urlSport;
      let drillModule;

      switch (sport) {
        case 'soccer':
          drillModule = await import('../data/drills/soccer-drills.js');
          break;
        case 'basketball':
          drillModule = await import('../data/drills/basketball-drills.js');
          break;
        case 'baseball':
          drillModule = await import('../data/drills/baseball-drills.js');
          break;
        case 'karate':
          drillModule = await import('../data/drills/karate-drills.js');
          break;
        case 'volleyball':
          drillModule = await import('../data/drills/volleyball-drills.js');
          break;
        case 'dance':
          drillModule = await import('../data/drills/dance-drills.js');
          break;
        case 'ballet':
          drillModule = await import('../data/drills/ballet-drills.js');
          break;
        case 'boxing':
          drillModule = await import('../data/drills/boxing-drills.js');
          break;
        case 'yoga':
          drillModule = await import('../data/drills/yoga-drills.js');
          break;
        case 'taekwondo':
          drillModule = await import('../data/drills/taekwondo-drills.js');
          break;
        case 'movement-coordination':
          drillModule = await import(
            '../data/drills/movement-coordination-drills.js'
          );
          break;
        case 'running-technique':
          drillModule = await import(
            '../data/drills/running-technique-drills.js'
          );
          break;
        case 'martial-arts':
          drillModule = await import('../data/drills/martial-arts-drills.js');
          break;
        default:
          drillModule = await import('../data/drills/soccer-drills.js');
      }

      const drill = drillModule.drills.find((d) => d.id === drillId);
      if (drill) {
        setDrillData(drill);
      }
    } catch (error) {
      console.error('Error loading drill data:', error);
    }
  };

  // Derive sport from skill data
  const sport = skillData ? skillData.sport : urlSport;

  // Audio file paths
  const getAudioPath = (type, exerciseName = null) => {
    let path = null;
    const currentDrillId = skillData?.drills[currentDrillIndex];

    // Check if this is a partner drill (drillId >= 1501)
    const isPartnerDrill = currentDrillId >= 1501;

    // Sport-specific drill audio folder mapping
    const regularMap = {
      soccer: 'latest-generated-drills-audio',
      basketball: 'latest-generated-basketball-drills-audio',
      baseball: 'latest-generated-baseball-drills-audio',
      karate: 'latest-generated-karate-drills-audio',
      taekwondo: 'latest-generated-taekwondo-drills-audio',
      volleyball: 'latest-generated-volleyball-drills-audio',
      'movement-coordination':
        'latest-generated-movement-coordination-drills-audio',
      'martial-arts': 'latest-generated-martial-arts-drills-audio',
      martial_arts: 'latest-generated-martial-arts-drills-audio',
      dance: 'latest-generated-dance-drills-audio',
      ballet: 'latest-generated-ballet-drills-audio',
      boxing: 'latest-generated-boxing-drills-audio',
      yoga: 'latest-generated-yoga-drills-audio',
      'running-technique': 'latest-generated-running-technique-drills-audio',
    };

    const audioFolder = isPartnerDrill
      ? 'latest-generated-partner-drills-audio'
      : regularMap[sport] || 'latest-generated-drills-audio';

    if (type === 'intro') {
      // Drill intro: /latest-generated-ballet-drills-audio/ballet_drill_2190_intro.mp3
      path = `/${audioFolder}/${sport}_${
        isPartnerDrill ? 'partner_drill' : 'drill'
      }_${currentDrillId}_intro.mp3`;
    } else if (type === 'conclusion') {
      // Drill conclusion: /latest-generated-ballet-drills-audio/ballet_drill_2190_conclusion.mp3
      path = `/${audioFolder}/${sport}_${
        isPartnerDrill ? 'partner_drill' : 'drill'
      }_${currentDrillId}_conclusion.mp3`;
    } else if (type === 'exercise' && exerciseName) {
      if (isPartnerDrill) {
        // Partner drill instructions: /latest-generated-partner-drills-audio/soccer_partner_drill_1501_instructions.mp3
        path = `/${audioFolder}/${sport}_partner_drill_${currentDrillId}_instructions.mp3`;
      } else {
        // Exercise audio: /latest-generated-exercises-audio/fastFeet.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}.mp3`;
      }
    } else if (type === 'exerciseIntro' && exerciseName) {
      if (isPartnerDrill) {
        // Partner drills don't have separate exercise intros, use the main instructions
        path = `/${audioFolder}/${sport}_partner_drill_${currentDrillId}_instructions.mp3`;
      } else {
        // Exercise intro audio: /latest-generated-exercises-audio/fastFeet_intro.mp3
        const exerciseFileName = convertExerciseNameToFileName(exerciseName);
        path = `/latest-generated-exercises-audio/${exerciseFileName}_intro.mp3`;
      }
    }

    return path;
  };

  // Convert exercise name to audio file name
  const convertExerciseNameToFileName = (exerciseName) => {
    // Map exercise names to their audio file names
    const nameMap = {
      'Fast Feet': 'fastFeet',
      'High Knees': 'highKnees',
      'Mountain Climbers': 'mountainClimbers',
      'Star Jumps': 'starJumps',
      'Skater Hops': 'skaterHops',
      'Bear Crawl': 'bearCrawl',
      'Big Arm Swings': 'bigArmSwings',
      'Pike Push-ups': 'pikePushUps',
      'Crab Walk': 'crabWalk',
      'One Leg Stand': 'oneLegStand',
      'Jumping Jacks': 'jumpingJacks',
      'Wall Sits': 'wallSits',
      'Superman Holds': 'supermanHolds',
      'Bridge Holds': 'bridgeHolds',
      Plank: 'plank',
      'Diamond Push-ups': 'diamondPushUps',
      Burpees: 'burpees',
      Squats: 'squats',
      Lunges: 'lunges',
      'Push-ups': 'pushUps',
      'Sit-ups': 'sitUps',
      'Leg Raises': 'legRaises',
      'Arm Circles': 'armCircles',
      'Neck Rolls': 'neckRolls',
      'Shoulder Shrugs': 'shoulderShrugs',
      'Hip Circles': 'hipCircles',
      'Ankle Rolls': 'ankleRolls',
      'Wrist Rolls': 'wristRolls',
      'Elbow Circles': 'elbowCircles',
      'Knee Circles': 'kneeCircles',
      'Toe Touches': 'toeTouches',
      Windmills: 'windmills',
      'Trunk Twists': 'trunkTwists',
      'Side Bends': 'sideBends',
      'Forward Bends': 'forwardBends',
      'Backward Bends': 'backwardBends',
      'Calf Raises': 'calfRaises',
      'Heel Walks': 'heelWalks',
      'Toe Walks': 'toeWalks',
      'Duck Walks': 'duckWalks',
      'Frog Jumps': 'frogJumps',
      'Rabbit Hops': 'rabbitHops',
      'Rainbow Reaches': 'rainbowReaches',
      'Dragon Walk': 'dragonWalk',
      'Bridge Builder': 'bridgeBuilder',
      'Robot Dance': 'robotDance',
      'Butterfly Flutter': 'butterflyFlutter',
      'Elephant Swing': 'elephantSwing',
      'Penguin Waddle': 'penguinWaddle',
      'Gorilla Walk': 'gorillaWalk',
      'Kangaroo Bounce': 'kangarooBounce',
      'Snake Slither': 'snakeSlither',
      'Bird Flap': 'birdFlap',
      'Fish Swim': 'fishSwim',
      'Horse Gallop': 'horseGallop',
      'Cat Stretch': 'catStretch',
      'Dog Wag': 'dogWag',
      'Monkey Climb': 'monkeyClimb',
      'Tiger Pounce': 'tigerPounce',
      'Lion Roar': 'lionRoar',
      'Zebra Trot': 'zebraTrot',
      'Giraffe Reach': 'giraffeReach',
      'Hippo Splash': 'hippoSplash',
      'Rhino Charge': 'rhinoCharge',
      'Elephant March': 'elephantMarch',
      'Penguin Slide': 'penguinSlide',
      'Dolphin Jump': 'dolphinJump',
      'Shark Swim': 'sharkSwim',
      'Whale Splash': 'whaleSplash',
      'Octopus Wave': 'octopusWave',
      'Crab Scuttle': 'crabScuttle',
      'Jellyfish Float': 'jellyfishFloat',
      'Starfish Stretch': 'starfishStretch',
      'Seahorse Bob': 'seahorseBob',
      'Turtle Crawl': 'turtleCrawl',
      'Snail Crawl': 'snailCrawl',
      'Ant March': 'antMarch',
      'Bee Buzz': 'beeBuzz',
      'Butterfly Float': 'butterflyFloat',
      'Dragonfly Hover': 'dragonflyHover',
      'Firefly Glow': 'fireflyGlow',
      'Ladybug Spot': 'ladybugSpot',
      'Spider Crawl': 'spiderCrawl',
      'Centipede Walk': 'centipedeWalk',
      'Worm Wiggle': 'wormWiggle',
      'Caterpillar Crawl': 'caterpillarCrawl',
      'Moth Flutter': 'mothFlutter',
      'Beetle Roll': 'beetleRoll',
      'Grasshopper Jump': 'grasshopperJump',
      'Cricket Chirp': 'cricketChirp',
      'Mosquito Buzz': 'mosquitoBuzz',
      'Fly Zoom': 'flyZoom',
      'Wasp Sting': 'waspSting',
      'Hornet Buzz': 'hornetBuzz',
      'Yellow Jacket': 'yellowJacket',
      'Paper Wasp': 'paperWasp',
      'Mud Dauber': 'mudDauber',
      'Potter Wasp': 'potterWasp',
      'Cicada Sing': 'cicadaSing',
      'Katydid Chirp': 'katydidChirp',
      'Stick Insect': 'stickInsect',
      'Praying Mantis': 'prayingMantis',
      'Walking Stick': 'walkingStick',
      'Leaf Insect': 'leafInsect',
      'Bark Bug': 'barkBug',
      'Tree Hopper': 'treeHopper',
      'Plant Hopper': 'plantHopper',
      'Aphid Crawl': 'aphidCrawl',
      'Scale Insect': 'scaleInsect',
      Mealybug: 'mealybug',
      Whitefly: 'whitefly',
      Thrips: 'thrips',
      Leafhopper: 'leafhopper',
      Treehopper: 'treehopper',
      Spittlebug: 'spittlebug',
      Froghopper: 'froghopper',
      'Jumping Plant Louse': 'jumpingPlantLouse',
      Psyllid: 'psyllid',
      Aleyrodid: 'aleyrodid',
      Coccid: 'coccid',
      Diaspidid: 'diaspidid',
      Eriococcid: 'eriococcid',
      Pseudococcid: 'pseudococcid',
      Ortheziid: 'ortheziid',
      Margarodid: 'margarodid',
      Monophlebidae: 'monophlebidae',
      Putoidae: 'putoidae',
      Phenacoleachiidae: 'phenacoleachiidae',
      Stictococcidae: 'stictococcidae',
      Beesoniidae: 'beesoniidae',
      Conchaspididae: 'conchaspididae',
      Halimococcidae: 'halimococcidae',
      Kermesidae: 'kermesidae',
      Kerriidae: 'kerriidae',
      Lecanodiaspididae: 'lecanodiaspididae',
      Micrococcidae: 'micrococcidae',
      Ortheziidae: 'ortheziidae',
      Phoenicococcidae: 'phoenicococcidae',
      Stictococcidae: 'stictococcidae',
      Aclerdidae: 'aclerdidae',
      Asterolecaniidae: 'asterolecaniidae',
      Cerococcidae: 'cerococcidae',
      Coccidae: 'coccidae',
      Dactylopiidae: 'dactylopiidae',
      Eriococcidae: 'eriococcidae',
      Kermidae: 'kermidae',
      Kerriidae: 'kerriidae',
      Lecanodiaspididae: 'lecanodiaspididae',
      Matsucoccidae: 'matsucoccidae',
      Monophlebidae: 'monophlebidae',
      Ortheziidae: 'ortheziidae',
      Phenacoleachiidae: 'phenacoleachiidae',
      Phoenicococcidae: 'phoenicococcidae',
      Pseudococcidae: 'pseudococcidae',
      Putoidae: 'putoidae',
      Stictococcidae: 'stictococcidae',
      Xylococcidae: 'xylococcidae',
      Aclerdidae: 'aclerdidae',
      Asterolecaniidae: 'asterolecaniidae',
      Cerococcidae: 'cerococcidae',
      Coccidae: 'coccidae',
      Dactylopiidae: 'dactylopiidae',
      Eriococcidae: 'eriococcidae',
      Kermidae: 'kermidae',
      Kerriidae: 'kerriidae',
      Lecanodiaspididae: 'lecanodiaspididae',
      Matsucoccidae: 'matsucoccidae',
      Monophlebidae: 'monophlebidae',
      Ortheziidae: 'ortheziidae',
      Phenacoleachiidae: 'phenacoleachiidae',
      Phoenicococcidae: 'phoenicococcidae',
      Pseudococcidae: 'pseudococcidae',
      Putoidae: 'putoidae',
      Stictococcidae: 'stictococcidae',
      Xylococcidae: 'xylococcidae',
    };

    return (
      nameMap[exerciseName] || exerciseName.toLowerCase().replace(/\s+/g, '')
    );
  };

  // Start the skill flow
  const startSkillFlow = () => {
    setShowStartScreen(false);
    setShowIntroScreen(true);
    playIntroAudio();
  };

  // Play intro audio
  const playIntroAudio = () => {
    if (!skillData) return;

    const introPath = getAudioPath('intro');
    const audioUrl = getVersionedAudioUrl(introPath);

    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current
        .play()
        .then(() => {
          setIsAudioPlaying(true);
        })
        .catch((error) => {
          console.error('Audio play error:', error);
          // If intro fails, start the first drill
          startFirstDrill();
        });
    }
  };

  // Start the first drill
  const startFirstDrill = () => {
    setShowIntroScreen(false);
    setCurrentDrillIndex(0);
    setCurrentExerciseIndex(0);
    setCurrentTime(0);
    setIsActive(true);
    startTimer();
  };

  // Start timer
  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setCurrentTime((prevTime) => {
        const newTime = prevTime + 1;

        // Check if we need to move to next exercise or drill
        if (drillData && drillData.timedInstructions) {
          const currentInstruction = drillData.timedInstructions.find(
            (instruction) => instruction.time === newTime
          );

          if (currentInstruction) {
            setCurrentExerciseData(currentInstruction);
          }
        }

        return newTime;
      });
    }, 1000);
  };

  // Move to next drill
  const moveToNextDrill = () => {
    const nextDrillIndex = currentDrillIndex + 1;

    if (nextDrillIndex < skillData.drills.length) {
      setCurrentDrillIndex(nextDrillIndex);
      setCurrentExerciseIndex(0);
      setCurrentTime(0);
      loadDrillData(skillData.drills[nextDrillIndex]);
      startTimer();
    } else {
      // All drills completed
      completeSkill();
    }
  };

  // Complete the skill
  const completeSkill = () => {
    setIsActive(false);
    setIsCompleted(true);
    setShowConfetti(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Add points
    if (addPoints && skillData) {
      addPoints(skillData.points);
    }

    // Save completion
    if (selectedPlayersData && selectedPlayersData.length > 0) {
      saveWorkoutCompletion({
        type: 'skill',
        skillId: skillData.skillId,
        sport: skillData.sport,
        points: skillData.points,
        duration: skillData.duration,
        players: selectedPlayersData,
        completedAt: new Date().toISOString(),
      });
    }

    // Hide confetti after 5 seconds
    setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
  };

  // Handle audio ended
  const handleAudioEnded = () => {
    setIsAudioPlaying(false);

    if (showIntroScreen) {
      startFirstDrill();
    }
  };

  // Handle audio error
  const handleAudioError = () => {
    console.log('Audio playback error');
    setIsAudioPlaying(false);

    if (showIntroScreen) {
      startFirstDrill();
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current drill progress
  const getCurrentDrillProgress = () => {
    if (!skillData) return 0;
    return ((currentDrillIndex + 1) / skillData.drills.length) * 100;
  };

  // Get total skill progress
  const getTotalSkillProgress = () => {
    if (!skillData || !drillData) return 0;
    const drillProgress = currentTime / drillData.timeTotal;
    const totalProgress =
      (currentDrillIndex + drillProgress) / skillData.drills.length;
    return totalProgress * 100;
  };

  if (!skillData) {
    return <div>Loading skill...</div>;
  }

  return (
    <div className='drill-screen'>
      {showConfetti && <Confetti />}

      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
      />

      {showStartScreen && (
        <div className='start-screen'>
          <h1>{skillData.title}</h1>
          <p>{skillData.description}</p>
          <div className='skill-info'>
            <p>Difficulty: {skillData.difficulty}</p>
            <p>Duration: {skillData.duration}</p>
            <p>Points: {skillData.points}</p>
            <p>Drills: {skillData.drills.length}</p>
          </div>
          <button onClick={startSkillFlow} className='start-button'>
            Start Skill
          </button>
          <button
            onClick={() => navigate(`/sport/${sport}`)}
            className='back-button'
          >
            Back to {sport}
          </button>
        </div>
      )}

      {showIntroScreen && (
        <div className='intro-screen'>
          <h2>Getting Ready...</h2>
          <p>Loading {skillData.title}</p>
          {isAudioPlaying && <p>Playing intro...</p>}
        </div>
      )}

      {isActive && drillData && (
        <div className='drill-content'>
          <div className='progress-bar'>
            <div
              className='progress-fill'
              style={{ width: `${getTotalSkillProgress()}%` }}
            ></div>
          </div>

          <div className='drill-header'>
            <h2>{skillData.title}</h2>
            <p>
              Drill {currentDrillIndex + 1} of {skillData.drills.length}
            </p>
            <p>Time: {formatTime(currentTime)}</p>
          </div>

          {currentExerciseData && (
            <div className='exercise-display'>
              <h3>{currentExerciseData.text}</h3>
            </div>
          )}

          <div className='drill-controls'>
            <button onClick={() => setIsActive(false)} className='pause-button'>
              Pause
            </button>
            <button onClick={moveToNextDrill} className='next-button'>
              Next Drill
            </button>
          </div>
        </div>
      )}

      {isCompleted && (
        <div className='completion-screen'>
          <h2>🎉 Skill Completed!</h2>
          <p>You've mastered {skillData.title}!</p>
          <p>Points earned: {skillData.points}</p>
          <button
            onClick={() => navigate(`/sport/${sport}`)}
            className='back-button'
          >
            Back to {sport}
          </button>
        </div>
      )}
    </div>
  );
};

export default SkillScreen;
